/*
  Resty HK sleep calculator with a light plan (sections/resty-hk-sleep-calculator.liquid), ported from the feelresty.com calculator of
  2026-10-29.

  Calculates in the browser and stores nothing. A sleep cycle lasts about 90 minutes on average; the calculator counts whole cycles plus the
  time it takes to fall asleep. The light plan follows the international recommendation for indoor light (Brown et al., PLoS Biol 2022):
  plenty of light during the day, dimmed light from about 3 hours before going to sleep and as dark as possible while sleeping.
*/
(() => {
  const CYCLE = 90;
  const CYCLES = [6, 5, 4];
  const RECOMMENDED = new Set([5, 6]);
  const EVENING_DIM = 180;
  const DAY = 1440;

  const toMinutes = (value) => {
    const [h, m] = String(value || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  };
  const wrap = (minutes) => ((minutes % DAY) + DAY) % DAY;

  class RestyHkSleepCalculator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      // The t filter escapes text for HTML (an apostrophe becomes &#39;); here the text reaches the page through textContent, so decode it.
      const config = this.querySelector('[data-rsc-config]');
      const decode = (value) => {
        const area = document.createElement('textarea');
        area.innerHTML = value;
        return area.value;
      };
      this.t = Object.fromEntries(Object.entries(config ? JSON.parse(config.textContent) : {}).map(([key, value]) => [key, decode(value)]));
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.plurals = new Intl.PluralRules(this.locale);
      this.number = new Intl.NumberFormat(this.locale, { maximumFractionDigits: 1 });
      this.clock = new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC' });
      this.form = this.querySelector('[data-rsc-form]');
      this.form.hidden = false;
      this.querySelector('[data-rsc-noscript]')?.remove();
      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.form.addEventListener('input', () => this.render());
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'rsc-mode') this.syncMode();
        this.render();
      });
      this.addEventListener('click', (event) => this.onClick(event));
      this.syncMode();
      this.render();
    }

    format(template, values) {
      return Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template || '');
    }

    plural(base, n) {
      return this.format(this.t[`${base}_${this.plurals.select(n)}`] || this.t[`${base}_other`], { n });
    }

    time(minutes) {
      return this.clock.format(new Date(Date.UTC(2026, 0, 1, 0, wrap(minutes))));
    }

    hours(minutes) {
      const value = minutes / 60;
      const rule = this.plurals.select(value);
      return this.format(this.t[`hours_${rule}`] || this.t.hours_other, { n: this.number.format(value) });
    }

    track(name, params) {
      try {
        window.Shopify?.analytics?.publish?.(name, { tool: 'sleep_calculator', tool_locale: this.locale, ...params });
      } catch (error) {}
    }

    get mode() {
      return this.form.querySelector('[name="rsc-mode"]:checked')?.value || 'wake';
    }

    syncMode() {
      this.querySelectorAll('[data-rsc-when]').forEach((node) => {
        node.hidden = node.dataset.rscWhen !== this.mode;
      });
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !target.matches('[data-rsc-now]')) return;
      const now = new Date();
      this.form.querySelector('[name="rsc-bed"]').value = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      this.render();
    }

    render() {
      const latency = Number(this.form.querySelector('[name="rsc-latency"]').value) || 15;
      const anchor = toMinutes(this.form.querySelector(`[name="${this.mode === 'wake' ? 'rsc-wake' : 'rsc-bed'}"]`).value);
      const list = this.querySelector('[data-rsc-times]');
      if (anchor === null) {
        list.replaceChildren();
        return;
      }
      // The first render shows the default times; the first change by the visitor counts as a result.
      if (!this.started) {
        this.started = true;
      } else if (!this.used) {
        this.used = true;
        this.track('resty_tool_result', { tool_mode: this.mode });
      }

      this.querySelector('[data-rsc-title]').textContent = this.mode === 'wake' ? this.t.bed_title : this.t.wake_title;
      const options = CYCLES.map((cycles) => {
        const sleep = cycles * CYCLE;
        const at = this.mode === 'wake' ? anchor - sleep - latency : anchor + latency + sleep;
        return { cycles, sleep, at };
      });

      list.replaceChildren(
        ...options.map((option) => {
          const item = document.createElement('li');
          item.className = `rsc-time${RECOMMENDED.has(option.cycles) ? ' is-recommended' : ''}`;
          const time = document.createElement('strong');
          time.className = 'rsc-time__at';
          time.textContent = this.time(option.at);
          const meta = document.createElement('span');
          meta.className = 'rsc-time__meta';
          meta.textContent = `${this.plural('cycles', option.cycles)} · ${this.hours(option.sleep)}`;
          item.append(time, meta);
          if (RECOMMENDED.has(option.cycles)) {
            const badge = document.createElement('span');
            badge.className = 'rsc-time__badge';
            badge.textContent = this.t.recommended;
            item.append(badge);
          } else {
            const short = document.createElement('span');
            short.className = 'rsc-time__short';
            short.textContent = this.t.short;
            item.append(short);
          }
          return item;
        }),
      );

      // The light plan around the option of 5 cycles (7.5 hours): the middle of the advice.
      const plan = options.find((option) => option.cycles === 5);
      const bed = this.mode === 'wake' ? plan.at : anchor;
      const wake = this.mode === 'wake' ? anchor : plan.at;
      const set = (key, value) => {
        const node = this.querySelector(`[data-rsc-plan="${key}"]`);
        if (node) node.textContent = value;
      };
      set('morning', this.format(this.t.plan_morning_time, { from: this.time(wake), to: this.time(wake + 60) }));
      set('evening', this.format(this.t.plan_evening_time, { from: this.time(bed - EVENING_DIM) }));
      set('night', this.format(this.t.plan_night_time, { from: this.time(bed) }));
    }
  }

  if (!customElements.get('resty-hk-sleep-calculator')) customElements.define('resty-hk-sleep-calculator', RestyHkSleepCalculator);
})();
